import { useCallback, useEffect, useRef, useState } from 'react';
import useLoginStore from 'store/loginStore';
import useNotificationStore from 'store/notificationStore';

export default function useFetch(url) {
  const [isLoading, setIsLoading] = useState(url != null);
  const [isReloading, setIsReloading] = useState(url != null);
  const [isNotFound, setIsNotFound] = useState(false);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [reloadCounter, setReloadCounter] = useState(0);
  const lastFetchedUrlRef = useRef();
  const addMessage = useNotificationStore(s => s.addMessage);
  const currentRole = useLoginStore(s => s.getCurrentRole)();

  const reload = () => {
    setReloadCounter((s) => s + 1);
    console.log('reload:', reloadCounter);
    // This advanced setIsReloading might be necessary in edge cases - leaving commented out until we really need it
    // setIsReloading(true);
  }

  // Helper function to add role parameter to admin endpoints
  const addRoleToAdminUrl = useCallback((url) => {
    if (!url || !currentRole) return url;
    
    // Check if this is an admin endpoint
    if (url.includes('/admin/')) {
      // Check if role parameter is already present
      if (!url.includes('role=')) {
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}role=${currentRole}`;
      }
    }
    return url;
  }, [currentRole]);

  const fetchData = useCallback(async (controller, options = {}) => {
    if (url) {
      if (reloadCounter > 0) {
        setIsReloading(true);
      } else {
        setIsLoading(true);
      }
      if (options.reset) {
        setData(null);
      }
      setStatus(null);
      setIsNotFound(false);
      setIsError(false);

      try {
        const startTime = performance.now();
        
        // Add role parameter to admin endpoints
        const finalUrl = addRoleToAdminUrl(url);

        const response = await fetch(finalUrl, {
          credentials: 'include',
          signal: controller.signal,
        });

        const endTime = performance.now();
        const loadingTime = endTime - startTime;
        
        // console.log('Fetch:', Math.round(loadingTime), 'ms');
        if (loadingTime > 1000) {
          console.log('Slow fetch:', Math.round(loadingTime), 'ms', { url: '/' + url.split('/').slice(3).join('/') });
        }


        setStatus(response.status);

        if (response.status == 200) {
          const responseJson = await response.json();
          setData(responseJson);
          setIsLoading(false);
          setIsReloading(false);
        } else if (response.status == 404) {
          setIsNotFound(true);
          setIsLoading(false);
          setIsReloading(false);
        } else {
          addMessage('Error loading data from server', 'error')
          setIsError(true);
          setIsLoading(false);
          setIsReloading(false);
        }
        
      } catch (e) {
        if (e.name != 'AbortError') {
          addMessage('Error loading data from server', 'error')
          console.error(e);
          setIsError(true);
        }
      }
    } else {
      controller.abort();

      setIsLoading(false);
      setIsReloading(false);
      setData(null);
      setStatus(null);
      setIsNotFound(false);
      setIsError(false);
    }
  }, [url, reloadCounter, addMessage, addRoleToAdminUrl]);

  // fetchData changes exactly when url / reloadCounter / role change (all in the same render) => one request per change.
  // (prevUrl from usePrevious used to be a dependency here - it changed one render later, so every URL change
  // sent the same request twice to the server.)
  useEffect(() => {
    let controller = new AbortController();

    const urlChanged = (lastFetchedUrlRef.current != url);
    lastFetchedUrlRef.current = url;

    fetchData(controller, { reset: urlChanged });

    return () => {
      controller.abort();
    }

  }, [url, fetchData]);

  return { data, status, isLoading, isReloading, isNotFound, isError, reload, reloadCounter };
}
